import {
    Box,
    Button,
    Card,
    Flex,
    HStack,
    Heading,
    Icon,
    SimpleGrid,
    Text,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
    MdAutoGraph,
    MdDownload,
    MdEuro,
    MdGroup,
    MdGroupAdd,
    MdReceiptLong,
    MdRefresh,
    MdSportsTennis,
} from 'react-icons/md';
import PageLayout from '../../components/page-layout';
import { StaggeredGrid, StaggeredItem } from '../../components/animations/StaggeredGrid';
import { translate } from '../../i18n';
import DateRangePicker from '../analytics/components/DateRangePicker';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ImpactMetricCard from './components/ImpactMetricCard';
import GrowthTrajectoryChart from './components/GrowthTrajectoryChart';
import RevenuePerPlayerCard from './components/RevenuePerPlayerCard';
import { useGetGrowthTrajectory } from './hooks/useGetGrowthTrajectory';
import { useGetImpactSummary } from './hooks/useGetImpactSummary';
import { useGetRevenuePerPlayer } from './hooks/useGetRevenuePerPlayer';
import { exportImpactPdf } from './utils/impactPdfExport';
import { UNKNOWN_STAT_VALUE, isUnreadable } from '../analytics/utils/statValue';
import { classifyRead } from '../_shared/readState';

type DateRangePickerIntervalType =
    | 'All Time'
    | 'Last 24 Hours'
    | 'Last 7 Days'
    | 'Last 30 Days'
    | 'Last 90 Days'
    | 'Custom';

type PresetInterval = Exclude<DateRangePickerIntervalType, 'All Time' | 'Custom'>;

const INTERVAL_DURATIONS_MS: Record<PresetInterval, number> = {
    'Last 24 Hours': 1 * 24 * 60 * 60 * 1000,
    'Last 7 Days': 7 * 24 * 60 * 60 * 1000,
    'Last 30 Days': 30 * 24 * 60 * 60 * 1000,
    'Last 90 Days': 90 * 24 * 60 * 60 * 1000,
};

function formatCurrency(value: number): string {
    return `${value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })} €`;
}

export default function ImpactPage() {
    const brandColor = useColorModeValue('brand.500', 'white');
    const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const textColorSecondary = 'secondaryGray.600';
    const heroBg = useColorModeValue('brand.50', 'whiteAlpha.100');
    const heroTextColor = useColorModeValue('brand.500', 'white');
    const toast = useToast();

    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();

    const [intervalType, setIntervalType] = useState<DateRangePickerIntervalType>('Last 30 Days');

    const [startDate, setStartDate] = useState<Date | undefined>(
        () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    );
    const [endDate, setEndDate] = useState<Date | undefined>(() => new Date());

    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    useEffect(() => {
        if (intervalType === 'Custom') {
            if (customStartDate && customEndDate) {
                const start = new Date(customStartDate);
                start.setHours(0, 0, 0, 0);
                const end = new Date(customEndDate);
                end.setHours(23, 59, 59, 999);
                setStartDate(start);
                setEndDate(end);
            }
            return;
        }
        if (intervalType === 'All Time') {
            setStartDate(undefined);
            setEndDate(undefined);
            return;
        }
        const now = new Date();
        now.setHours(23, 59, 59, 999);
        const durationMs = INTERVAL_DURATIONS_MS[intervalType];
        setStartDate(new Date(now.getTime() - durationMs));
        setEndDate(now);
    }, [intervalType, customStartDate, customEndDate]);

    const {
        data: summary,
        isInitialLoading: isInitialLoadingSummary,
        isError: isSummaryError,
    } = useGetImpactSummary(selectedLocation?.id, startDate, endDate);
    /**
     * `analytics/impact-summary` carries `@UseGuards(AdminAuthGuard)`
     * (`api/src/analytics/analytics.controller.ts`, read this run), so an
     * operator who is no longer an admin of the selected org is refused with no
     * outage at all. Four of the six cards below read `summary?.x ?? 0`, so
     * that refusal used to print a real `0` for bookings, self-service
     * bookings, recovered spots and new players — on the page whose entire
     * purpose is to tell the operator how much the product did for them.
     */
    /**
     * Both states from ONE classification of the query's own flags (R102-1,
     * closing D62). The predicate this replaced restated the hook's
     * `enabled: !!locationId` here, in a second file — correct on the day it
     * was written and silently wrong the day the hook's gate grows a second
     * clause, in the direction that shows a loading affordance forever.
     */
    const summaryReadState = classifyRead(isSummaryError, isInitialLoadingSummary, summary);
    const summaryUnreadable = summaryReadState === 'unreadable';
    const summaryPending = summaryReadState === 'loading';
    /**
     * FOUND BY THE RUN-103 CENSUS, and it is run 97's defect surviving in the
     * other arm. Four of the six cards below read
     * `summaryUnreadable ? UNKNOWN_STAT_VALUE : summary?.x ?? 0`. A
     * NEVER-ISSUED read is not unreadable and not pending, so with no location
     * selected all four printed a literal **`0`** — "0 bookings, 0 self-service
     * bookings, 0 spots recovered, 0 new players" — permanently, on the page
     * whose entire purpose is to tell the operator what the product did for
     * them. Their two revenue siblings in the same grid were already correct
     * (`summary ? formatCurrency(…) : UNKNOWN_STAT_VALUE`), which is what makes
     * this an oversight rather than a decision.
     *
     * A number is the worst shape for this class (v1.41): a missing list
     * renders empty and an operator can disbelieve it, while a missing scalar
     * renders as whatever the `?? 0` beside it says and carries no trace of its
     * own absence.
     */
    const summaryAnswered = summaryReadState === 'answered';
    const summaryCount = (value: number | undefined) =>
        summaryAnswered ? value ?? 0 : UNKNOWN_STAT_VALUE;
    const { data: topPlayers, isError: isTopPlayersError } = useGetRevenuePerPlayer(
        selectedLocation?.id,
        startDate,
        endDate,
        10,
    );
    const { data: growth, isError: isGrowthError } = useGetGrowthTrajectory(selectedLocation?.id);
    /**
     * These two feed the PDF only. `topPlayers ?? []` and `growth ?? []` turned
     * a REFUSED read into an empty section of a document the operator sends to
     * their board — a leaderboard with nobody on it and a flat growth chart,
     * with nothing anywhere on the page saying the data was not read. An empty
     * section is legitimate for a new venue, which is why the export refuses on
     * `isUnreadable` (refused with nothing to show) rather than on emptiness.
     */
    const supportingReadsUnreadable =
        isUnreadable(isTopPlayersError, topPlayers) || isUnreadable(isGrowthError, growth);

    const handleExportPdf = () => {
        if (!summary || !selectedOrganization || !selectedLocation) {
            toast({
                title: translate('impact.no_location'),
                status: 'warning',
                duration: 3000,
            });
            return;
        }
        if (supportingReadsUnreadable) {
            toast({
                title: translate('common.unexpected_error'),
                status: 'error',
                duration: 3000,
            });
            return;
        }
        const exportStart = startDate ?? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const exportEnd = endDate ?? new Date();
        exportImpactPdf({
            organizationName: selectedOrganization.name,
            locationName: selectedLocation.name,
            startDate: exportStart,
            endDate: exportEnd,
            summary,
            topPlayers: topPlayers ?? [],
            growth: growth ?? [],
        });
    };

    if (!selectedLocation) {
        return (
            <PageLayout>
                <Card p='40px'>
                    <Text color={textColor} fontSize='md' textAlign='center'>
                        {translate('impact.no_location')}
                    </Text>
                </Card>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            {/* Header with title + date range + PDF export */}
            <Card p='16px'>
                <Flex
                    align={{ base: 'stretch', md: 'center' }}
                    direction={{ base: 'column', md: 'row' }}
                    gap='12px'
                    flexWrap='wrap'
                >
                    <Box flex='1'>
                        <Heading as='h1' size='lg' color={textColor}>
                            {translate('impact.title')}
                        </Heading>
                        <Text color={textColorSecondary} fontSize='sm' mt='4px'>
                            {translate('impact.subtitle')}
                        </Text>
                    </Box>
                    <HStack spacing='12px'>
                        <DateRangePicker
                            intervalType={intervalType}
                            setIntervalType={setIntervalType}
                            customStartDate={customStartDate}
                            customEndDate={customEndDate}
                            onCustomStartDateChange={setCustomStartDate}
                            onCustomEndDateChange={setCustomEndDate}
                        />
                        <Button
                            leftIcon={<Icon as={MdDownload} />}
                            onClick={handleExportPdf}
                            colorScheme='brand'
                            variant='solid'
                            isDisabled={!summary}
                        >
                            {translate('impact.export_pdf')}
                        </Button>
                    </HStack>
                </Flex>
            </Card>

            {/* Counterfactual hero banner */}
            <Card p='24px' bg={heroBg}>
                <Flex align='center' gap='16px' direction={{ base: 'column', md: 'row' }}>
                    <Icon as={MdAutoGraph} w='48px' h='48px' color={heroTextColor} />
                    <Box>
                        <Text color={textColorSecondary} fontSize='sm' textTransform='uppercase' letterSpacing='0.05em'>
                            {translate('impact.recovered_revenue')}
                        </Text>
                        <Heading as='div' size='2xl' color={heroTextColor} letterSpacing='-0.03em'>
                            {summary ? formatCurrency(summary.recoveredRevenue) : UNKNOWN_STAT_VALUE}
                        </Heading>
                        <Text
                            color={summaryUnreadable ? 'danger.500' : textColorSecondary}
                            fontSize='sm'
                            mt='4px'
                            role={summaryUnreadable ? 'status' : undefined}
                        >
                            {summaryUnreadable
                                ? translate('common.unexpected_error')
                                : translate('impact.recovered_revenue_hint')}
                        </Text>
                    </Box>
                </Flex>
            </Card>

            {/* KPI grid */}
            <StaggeredGrid staggerDelay={0.06}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 6 }} gap='20px'>
                    <StaggeredItem>
                        <ImpactMetricCard
                            boxColor={boxBg}
                            brandColor={brandColor}
                            icon={MdSportsTennis}
                            name={translate('impact.total_bookings')}
                            value={summaryCount(summary?.totalBookings)}
                            isLoading={summaryPending}
                            isError={summaryUnreadable}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <ImpactMetricCard
                            boxColor={boxBg}
                            brandColor={brandColor}
                            icon={MdReceiptLong}
                            name={translate('impact.self_service_bookings')}
                            value={summaryCount(summary?.selfServiceBookings)}
                            isLoading={summaryPending}
                            isError={summaryUnreadable}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <ImpactMetricCard
                            boxColor={boxBg}
                            brandColor={brandColor}
                            icon={MdRefresh}
                            name={translate('impact.spots_recovered')}
                            value={summaryCount(summary?.replacementsFulfilled)}
                            isLoading={summaryPending}
                            isError={summaryUnreadable}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <ImpactMetricCard
                            boxColor={boxBg}
                            brandColor={brandColor}
                            icon={MdGroupAdd}
                            name={translate('impact.new_players')}
                            value={summaryCount(summary?.newPlayers)}
                            isLoading={summaryPending}
                            isError={summaryUnreadable}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <ImpactMetricCard
                            boxColor={boxBg}
                            brandColor={brandColor}
                            icon={MdEuro}
                            name={translate('impact.total_revenue')}
                            value={summary ? formatCurrency(summary.totalRevenue) : UNKNOWN_STAT_VALUE}
                            isLoading={summaryPending}
                            isError={summaryUnreadable}
                        />
                    </StaggeredItem>
                    <StaggeredItem>
                        <ImpactMetricCard
                            boxColor={boxBg}
                            brandColor={brandColor}
                            icon={MdGroup}
                            name={translate('impact.recovered_revenue')}
                            value={summary ? formatCurrency(summary.recoveredRevenue) : UNKNOWN_STAT_VALUE}
                            isLoading={summaryPending}
                            isError={summaryUnreadable}
                        />
                    </StaggeredItem>
                </SimpleGrid>
            </StaggeredGrid>

            {/* Growth chart + leaderboard */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap='20px'>
                <GrowthTrajectoryChart />
                <RevenuePerPlayerCard
                    startDate={startDate}
                    endDate={endDate}
                    limit={10}
                />
            </SimpleGrid>
        </PageLayout>
    );
}
